/**
 * Shared helpers for the row-selection toolbar actions on Cards and Sealed
 * inventory views:
 *
 *   - Generate Trade -> opens a new trade form, pre-fills Outgoing[] with the
 *     selected lots. User then adds incoming items + cash and saves.
 *
 *   - Generate Sale  -> opens a new sale form, pre-fills Items[] with the
 *     selected lots. User then sets Sale Price and saves.
 *
 *   - Generate Rip   -> existing single-lot rip flow, exposed here too so
 *     toolbar wiring lives in one place. Only valid when exactly 1 sealed
 *     lot is selected (rip is per-product).
 *
 * Each helper opens the form via the existing editTrade() / editSale()
 * entry points, awaits the form's async initialization, then mutates the
 * in-flight state object that the form keeps on `window`. The form's own
 * render hooks (`renderTradeOutgoingList`, `renderSaleItemsList`) repaint
 * the items list so the user sees the prefilled rows immediately.
 *
 * Pre-fill values:
 *   - Qty defaults to the lot's current Qty (so "sell the whole stack" works
 *     in one click; user can edit down for partial sales).
 *   - Per-unit Basis / FMV / Sale Price are derived from the lot's totals
 *     divided by Qty - matching the existing single-lot add helpers.
 *   - Item display string mirrors what the lot-search helpers produce.
 */
import { DB } from "./db";
import { toast } from "./dom";
import { editTrade } from "../forms/trade";
import { editSale } from "../forms/sale";
import { cardLockReason } from "./lotLock";

type LotKind = "card" | "sealed";

interface LotSummary {
  lotId:    string;
  kind:     LotKind;
  itemName: string;
  qty:      number;
  unitBasis: number;
  unitFMV:   number;
}

/** Look up a single lot and compute its per-unit basis / FMV. Returns null
 *  when the lot can't be found or has zero qty (no inventory to act on). */
function summarizeLot(lotId: string, kind: LotKind): LotSummary | null {
  const row = kind === "card"
    ? DB.table("cards").find(r => r["Card Lot ID"] === lotId)
    : DB.table("sealed").find(r => r["Lot ID"] === lotId);
  if (!row) return null;
  const qty = Number(row["Qty"]) || 0;
  if (qty <= 0) return null;
  const totalBasis = Number(row["Total Basis"]) || 0;
  const totalFMV   = Number(row["Current FMV"])  || 0;
  const itemName = kind === "card"
    ? [row["Card Name"] || "Card", row["Set"], row["Card #"] ? `#${row["Card #"]}` : ""]
        .filter(Boolean).join(" · ")
    : [row["Product"] || "Sealed product", row["Set"]]
        .filter(Boolean).join(" · ");
  return {
    lotId,
    kind,
    itemName,
    qty,
    unitBasis: +(totalBasis / qty).toFixed(2),
    unitFMV:   +(totalFMV   / qty).toFixed(2),
  };
}

/** Resolve a list of lot IDs to summary records, dropping anything invalid.
 *  Toasts a friendly error and returns null when none of the lots resolved. */
function summarizeLots(lotIds: string[], kind: LotKind): LotSummary[] | null {
  const out: LotSummary[] = [];
  const locked: string[] = [];
  for (const id of lotIds) {
    if (kind === "card" && cardLockReason(id)) { locked.push(id); continue; }
    const s = summarizeLot(id, kind);
    if (s) out.push(s);
  }
  if (locked.length) {
    toast(`${locked.length} card${locked.length === 1 ? "" : "s"} skipped \u2014 out for grading/repair until Returned.`, "err");
  }
  if (!out.length) {
    if (!locked.length) toast("None of the selected lots have stock left.", "err");
    return null;
  }
  return out;
}

/* ============================================================
   Generate Trade  - card or sealed lots become Outgoing items
   ============================================================ */
export async function generateTradeFromLots(lotIds: string[], kind: LotKind): Promise<void> {
  if (!lotIds.length) { toast("Select at least one lot first.", "err"); return; }
  const lots = summarizeLots(lotIds, kind);
  if (!lots) return;

  // Open a fresh trade form (defaults to Status=Accepted). editTrade resolves
  // once the modal is mounted and _tradeState is set on window.
  await editTrade(undefined);
  const state = (window as any)._tradeState;
  if (!state || !Array.isArray(state.Outgoing)) {
    toast("Couldn't open the trade form.", "err");
    return;
  }
  for (const l of lots) {
    state.Outgoing.push({
      "Lot ID": l.lotId,
      "Type":   l.kind === "card" ? "Card" : "Sealed",
      "Item":   l.itemName,
      "Qty":    l.qty,
      "Basis":  l.unitBasis,
      "FMV":    l.unitFMV,
    });
  }
  (window as any).renderTradeOutgoingList?.();
  (window as any).markModalDirty?.();
  toast(`Added ${lots.length} lot${lots.length === 1 ? "" : "s"} as outgoing items.`);
}

/* ============================================================
   Generate Sale  - card or sealed lots become Sale items
   ============================================================ */
export async function generateSaleFromLots(lotIds: string[], kind: LotKind): Promise<void> {
  if (!lotIds.length) { toast("Select at least one lot first.", "err"); return; }
  const lots = summarizeLots(lotIds, kind);
  if (!lots) return;

  await editSale(undefined);
  const state = (window as any)._saleState;
  if (!state || !Array.isArray(state.Items)) {
    toast("Couldn't open the sale form.", "err");
    return;
  }
  for (const l of lots) {
    state.Items.push({
      "Lot ID":     l.lotId,
      "Item":       l.itemName,
      "Type":       l.kind === "card" ? "Card" : "Sealed",
      "Qty":        l.qty,
      // Sale Price defaults to per-unit FMV (matches _addSaleItemFromLot's
      // convention - sale rows scale Qty * Sale Price internally). User
      // sets the actual sold price before saving.
      "Sale Price": l.unitFMV,
      "COGS":       l.unitBasis,
    });
  }
  (window as any).renderSaleItemsList?.();
  (window as any).markModalDirty?.();
  toast(`Added ${lots.length} lot${lots.length === 1 ? "" : "s"} as sale items.`);
}

/* ============================================================
   Generate Rip  - single-lot pass-through to existing rip flow
   ============================================================ */
export function generateRipFromLots(lotIds: string[]): void {
  if (lotIds.length !== 1) {
    toast("Pick exactly one sealed lot to rip.", "err");
    return;
  }
  const fn = (window as any).startRip;
  if (typeof fn !== "function") {
    toast("Rip flow isn't loaded.", "err");
    return;
  }
  fn(lotIds[0]);
}

// Window bridges so inline onclick handlers in toolbar HTML can call us.
(window as any).generateTradeFromLots = generateTradeFromLots;
(window as any).generateSaleFromLots  = generateSaleFromLots;
(window as any).generateRipFromLots   = generateRipFromLots;
